import React, {useEffect, useState} from "react";
import {Box, ButtonGroup, Heading, IconButton, Pagination, SimpleGrid, Table, Tabs, Text} from "@chakra-ui/react";
import {LuChevronLeft, LuChevronRight, LuCoins, LuDock, LuLibrary, LuUser} from "react-icons/lu";
import {DateTime} from "luxon";
import {CompanyExecutorFull, dataApi, Payout as Pay} from "../../api/data-api";
import {errorNotify} from "../../components/hooks/CommonUtils";
import {useToast} from "@chakra-ui/toast";
import ExecutorContract from "./executorContract";
import {DocumentList} from "../Document/documentList";
import {useRecoilState} from "recoil";
import {companyData} from "../../components/hooks/recoilData";

export default function ExecutorItem(props: {
    executor: CompanyExecutorFull,
}) {
    const [items, setItems] = useState<Pay[]>([]);
    const [pageSize] = useState<number>(10);
    const [count, setCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const toast = useToast();
    const [company] = useRecoilState(companyData);

    useEffect(() => {
        if (props.executor) {
            dataApi.payoutLstListByExecutor(props.executor.ID, (currentPage - 1) * pageSize, pageSize)
                .then((o) => setItems(o))
                .catch((err) => errorNotify(err, toast));
        }
    }, [props, toast, currentPage, pageSize]);

    useEffect(() => {
        if (props.executor) {
            dataApi.payoutLstListCountByExecutor(props.executor.ID)
                .then((o) => {
                    if (o)
                        setCount(o as number);
                })
                .catch((err) => errorNotify(err, toast));
        }
    }, [props, toast]);

    return (
        <Box borderWidth={1} borderColor="gray">
            <Heading size="xl">Исполнитель {props.executor.ID}</Heading>
            <Tabs.Root defaultValue="Payments">
                <Tabs.List>
                    <Tabs.Trigger value="Payments">
                        <LuCoins/>
                        Выплаты
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Contracts">
                        <LuDock/>
                        Контракты
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Documents">
                        <LuLibrary/>
                        Документы
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Data">
                        <LuUser/>
                        Данные
                    </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="Payments">
                    <Table.Root size="sm" variant="outline" striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>Создан</Table.ColumnHeader>
                                <Table.ColumnHeader>Номер реестра</Table.ColumnHeader>
                                <Table.ColumnHeader>Сумма</Table.ColumnHeader>
                                <Table.ColumnHeader>Налог</Table.ColumnHeader>
                                <Table.ColumnHeader>Комиссия</Table.ColumnHeader>
                                <Table.ColumnHeader>Отправлен</Table.ColumnHeader>
                                <Table.ColumnHeader>Обработан</Table.ColumnHeader>
                                <Table.ColumnHeader>Отменен</Table.ColumnHeader>
                                <Table.ColumnHeader>Налог оплачен</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {items.map((item) => (
                                <Table.Row key={item.ID}>
                                    <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                    <Table.Cell>{item.PAYOUT_LIST_ID}</Table.Cell>
                                    <Table.Cell>{item.VALUE1}</Table.Cell>
                                    <Table.Cell>{item.TAX}</Table.Cell>
                                    <Table.Cell>{item.COMMISSION}</Table.Cell>
                                    <Table.Cell>{item.SENDED ? DateTime.fromMillis(item.SENDED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                    <Table.Cell>{item.PROCESSED ? DateTime.fromMillis(item.PROCESSED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                    <Table.Cell>{item.REJECTED ? DateTime.fromMillis(item.REJECTED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                    <Table.Cell>{item.TAX_PAID ? DateTime.fromMillis(item.TAX_PAID).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>

                    <Pagination.Root count={count} pageSize={pageSize} page={currentPage}>
                        <ButtonGroup variant="ghost" size="sm" wrap="wrap">
                            <Pagination.PrevTrigger asChild>
                                <IconButton>
                                    <LuChevronLeft/>
                                </IconButton>
                            </Pagination.PrevTrigger>

                            <Pagination.Items
                                render={(page) => (
                                    <IconButton variant={{base: "ghost", _selected: "outline"}} onClick={e => setCurrentPage(page.value)}>
                                        {page.value}
                                    </IconButton>
                                )}
                            />

                            <Pagination.NextTrigger asChild>
                                <IconButton>
                                    <LuChevronRight/>
                                </IconButton>
                            </Pagination.NextTrigger>
                        </ButtonGroup>
                    </Pagination.Root>
                </Tabs.Content>
                <Tabs.Content value="Contracts">
                    <ExecutorContract executor={props.executor as any} isCompany={true} show={true}/>
                </Tabs.Content>
                <Tabs.Content value="Documents">
                    <DocumentList companies={company ? [company] : []} documentDirectionType={"TO"} companyId={company ? company.ID : null}
                                  userId={props.executor.USER_ID_OWN} editable={false}/>
                </Tabs.Content>
                <Tabs.Content value="Data">
                    <Box borderWidth={1} borderColor="gray">
                        <Heading>Персональные данные</Heading>
                        <SimpleGrid columns={[2]} gap={3}>
                            <Box>
                                <Text>Дата рождения</Text>
                            </Box>
                            <Box>
                                <Text>{DateTime.fromMillis(props.executor.DATE_OF_BIRTH).toFormat('dd.MM.yyyy')}</Text>
                            </Box>
                            <Box>
                                <Text>Место рождения</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.PASS_PLACE_OF_BIRTH}</Text>
                            </Box>
                            <Box>
                                <Text>Гражданство</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.COUNTRY}</Text>
                            </Box>
                            <Box>
                                <Text>ИНН</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.CODE_INN}</Text>
                            </Box>
                        </SimpleGrid>
                        <Heading>Контактные данные</Heading>
                        <SimpleGrid columns={[2]} gap={3}>
                            <Box>
                                <Text>Телефон</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.TEL}</Text>
                            </Box>
                            <Box>
                                <Text>Адресс регистрации</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.PASS_ADDRESS}</Text>
                            </Box>
                            <Box>
                                <Text>Email</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.EMAIL}</Text>
                            </Box>
                        </SimpleGrid>
                        <Heading>Паспорт</Heading>
                        <SimpleGrid columns={[2]} gap={3}>
                            <Box>
                                <Text>Серия / номер</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.PASS_NUMBER}</Text>
                            </Box>
                            <Box>
                                <Text>Код подразделения</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.PASS_DEPARTMENT_CODE}</Text>
                            </Box>
                            <Box>
                                <Text>Дата выдачи</Text>
                            </Box>
                            <Box>
                                <Text>{DateTime.fromMillis(props.executor.PASS_DATE).toFormat('dd.MM.yyyy')}</Text>
                            </Box>
                            <Box>
                                <Text>Кем выдан</Text>
                            </Box>
                            <Box>
                                <Text>{props.executor.PASS_DEPARTMENT}</Text>
                            </Box>
                        </SimpleGrid>
                    </Box>
                </Tabs.Content>
            </Tabs.Root>
        </Box>
    );
}